import { Button } from "@/components/ui/Button";
import classes from "./Form.module.scss";
import { useRef, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";
import { DetailsStep } from "@/types/Navigation";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { useTurnstile } from "@/hooks/useTurnstile";
import { usePayment } from "@/payment/usePayment";
import type { CardDetailsRef } from "@/payment/types";
import { getDriver } from "@/payment/driverRegistry";
import { useDriverStatus } from "@/payment/useDriverStatus";
import { Alert } from "@/components/ui/Alert";
import { useDonationFormStore } from "@/stores";
import { DonationAmountConfirmation } from "./DonationAmountConfirmation";

type FormStepCardDetailsProps = {
  onStepSubmit: () => void;
  hideSubmitButton?: boolean;
};

export function FormStepCardDetails(props: FormStepCardDetailsProps) {
  const { onStepSubmit, hideSubmitButton = false } = props;
  const t = useTranslation("_common");
  const { isReady } = useTurnstile();
  const { session } = usePayment();
  const { error: driverError } = useDriverStatus();
  const { donationAmount } = useDonationFormStore();

  const cardDetailsRef = useRef<CardDetailsRef>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async () => {
    setProcessing(true);

    try {
      const validation = await cardDetailsRef.current?.validate();

      if (validation?.isValid) {
        onStepSubmit();
        dispatchEvent(EventName.STEP_SUBMIT, {
          step_name: DetailsStep.CardDetails,
          data: {},
        });
      }
    } finally {
      setProcessing(false);
    }
  };

  if (driverError) {
    return (
      <div>
        <Alert variant="error">{t("3ds-unavailable")}</Alert>
      </div>
    );
  }

  const driver = session ? getDriver(session.gateway) : null;

  return (
    <div>
      {driver && (
        <driver.CardDetails ref={cardDetailsRef} amount={donationAmount ?? 0} />
      )}

      <DonationAmountConfirmation />
      <TurnstileWidget />

      {!hideSubmitButton && (
        <div className={classes.pagination}>
          <Button
            size="large"
            disabled={processing || !isReady}
            onClick={handleSubmit}
          >
            {t("complete-donation")}
          </Button>
        </div>
      )}
    </div>
  );
}
